import { useMutation, useQuery } from '@tanstack/react-query';
import type { MutationFunction, QueryFunction, QueryKey, UseMutationOptions, UseMutationResult, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import type {
  BrokerNerdsBarkerCoreEnumsMarketplace,
  BrokerNerdsBarkerCoreModelsAdministrativeValidateIntegrationRequest,
  BrokerNerdsBarkerCoreModelsAdministrativeValidateIntegrationResponse,
  BrokerNerdsBarkerCoreModelsMarketplacesEvent,
  BrokerNerdsBarkerCoreModelsMarketplacesGetListingsResponse,
  BrokerNerdsBarkerCoreModelsMarketplacesGetSeatingChartResponseWithCategories,
  BrokerNerdsBarkerCoreModelsPricingRuleFilters,
  BrokerNerdsBarkerCoreModelsWarehouseListingSnapshot,
  BrokerNerdsBarkerOrleansStateIntervalOverride,
  GetApiMarketplacesMarketplaceIdEventsEventIdListingsParams,
  GetApiMarketplacesMarketplaceIdEventsEventIdSeatingChartParams,
  GetApiMarketplacesMarketplaceIdEventsEventIdSectionAliasesParams,
  GetApiMarketplacesMarketplaceIdEventsParams,
  PutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFilteredParams,
} from './models';

export const putApiMarketplacesMarketplaceIdValidate = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  brokerNerdsBarkerCoreModelsAdministrativeValidateIntegrationRequest: BrokerNerdsBarkerCoreModelsAdministrativeValidateIntegrationRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsAdministrativeValidateIntegrationResponse>> => {
  return axios.put(`/api/Marketplaces/${marketplaceId}/Validate`, brokerNerdsBarkerCoreModelsAdministrativeValidateIntegrationRequest, options);
};

export const getPutApiMarketplacesMarketplaceIdValidateMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdValidate>>,
    TError,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; data: BrokerNerdsBarkerCoreModelsAdministrativeValidateIntegrationRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdValidate>>,
  TError,
  { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; data: BrokerNerdsBarkerCoreModelsAdministrativeValidateIntegrationRequest },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdValidate>>,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; data: BrokerNerdsBarkerCoreModelsAdministrativeValidateIntegrationRequest }
  > = (props) => {
    const { marketplaceId, data } = props ?? {};

    return putApiMarketplacesMarketplaceIdValidate(marketplaceId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutApiMarketplacesMarketplaceIdValidateMutationResult = NonNullable<Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdValidate>>>;
export type PutApiMarketplacesMarketplaceIdValidateMutationBody = BrokerNerdsBarkerCoreModelsAdministrativeValidateIntegrationRequest;
export type PutApiMarketplacesMarketplaceIdValidateMutationError = AxiosError<void>;

export const usePutApiMarketplacesMarketplaceIdValidate = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdValidate>>,
    TError,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; data: BrokerNerdsBarkerCoreModelsAdministrativeValidateIntegrationRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdValidate>>,
  TError,
  { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; data: BrokerNerdsBarkerCoreModelsAdministrativeValidateIntegrationRequest },
  TContext
> => {
  const mutationOptions = getPutApiMarketplacesMarketplaceIdValidateMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getApiMarketplacesMarketplaceIdEventsEventIdSeatingChart = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  params?: GetApiMarketplacesMarketplaceIdEventsEventIdSeatingChartParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsMarketplacesGetSeatingChartResponseWithCategories>> => {
  return axios.get(`/api/Marketplaces/${marketplaceId}/Events/${eventId}/SeatingChart`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getGetApiMarketplacesMarketplaceIdEventsEventIdSeatingChartQueryKey = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  params?: GetApiMarketplacesMarketplaceIdEventsEventIdSeatingChartParams,
) => {
  return [`/api/Marketplaces/${marketplaceId}/Events/${eventId}/SeatingChart`, ...(params ? [params] : [])] as const;
};

export const getGetApiMarketplacesMarketplaceIdEventsEventIdSeatingChartQueryOptions = <
  TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSeatingChart>>,
  TError = AxiosError<void>,
>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  params?: GetApiMarketplacesMarketplaceIdEventsEventIdSeatingChartParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSeatingChart>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiMarketplacesMarketplaceIdEventsEventIdSeatingChartQueryKey(marketplaceId, eventId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSeatingChart>>> = ({ signal }) =>
    getApiMarketplacesMarketplaceIdEventsEventIdSeatingChart(marketplaceId, eventId, params, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!(marketplaceId && eventId), ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSeatingChart>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetApiMarketplacesMarketplaceIdEventsEventIdSeatingChartQueryResult = NonNullable<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSeatingChart>>>;
export type GetApiMarketplacesMarketplaceIdEventsEventIdSeatingChartQueryError = AxiosError<void>;

export const useGetApiMarketplacesMarketplaceIdEventsEventIdSeatingChart = <
  TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSeatingChart>>,
  TError = AxiosError<void>,
>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  params?: GetApiMarketplacesMarketplaceIdEventsEventIdSeatingChartParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSeatingChart>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiMarketplacesMarketplaceIdEventsEventIdSeatingChartQueryOptions(marketplaceId, eventId, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getApiMarketplacesMarketplaceIdEventsEventIdListings = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  params?: GetApiMarketplacesMarketplaceIdEventsEventIdListingsParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsMarketplacesGetListingsResponse>> => {
  return axios.get(`/api/Marketplaces/${marketplaceId}/Events/${eventId}/Listings`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getGetApiMarketplacesMarketplaceIdEventsEventIdListingsQueryKey = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  params?: GetApiMarketplacesMarketplaceIdEventsEventIdListingsParams,
) => {
  return [`/api/Marketplaces/${marketplaceId}/Events/${eventId}/Listings`, ...(params ? [params] : [])] as const;
};

export const getGetApiMarketplacesMarketplaceIdEventsEventIdListingsQueryOptions = <
  TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdListings>>,
  TError = AxiosError<void>,
>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  params?: GetApiMarketplacesMarketplaceIdEventsEventIdListingsParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdListings>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiMarketplacesMarketplaceIdEventsEventIdListingsQueryKey(marketplaceId, eventId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdListings>>> = ({ signal }) =>
    getApiMarketplacesMarketplaceIdEventsEventIdListings(marketplaceId, eventId, params, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!(marketplaceId && eventId), ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdListings>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetApiMarketplacesMarketplaceIdEventsEventIdListingsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdListings>>>;
export type GetApiMarketplacesMarketplaceIdEventsEventIdListingsQueryError = AxiosError<void>;

export const useGetApiMarketplacesMarketplaceIdEventsEventIdListings = <
  TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdListings>>,
  TError = AxiosError<void>,
>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  params?: GetApiMarketplacesMarketplaceIdEventsEventIdListingsParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdListings>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiMarketplacesMarketplaceIdEventsEventIdListingsQueryOptions(marketplaceId, eventId, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getApiMarketplacesMarketplaceIdEventsEventIdSectionAliases = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  params?: GetApiMarketplacesMarketplaceIdEventsEventIdSectionAliasesParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<string[]>> => {
  return axios.get(`/api/Marketplaces/${marketplaceId}/Events/${eventId}/SectionAliases`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getGetApiMarketplacesMarketplaceIdEventsEventIdSectionAliasesQueryKey = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  params?: GetApiMarketplacesMarketplaceIdEventsEventIdSectionAliasesParams,
) => {
  return [`/api/Marketplaces/${marketplaceId}/Events/${eventId}/SectionAliases`, ...(params ? [params] : [])] as const;
};

export const getGetApiMarketplacesMarketplaceIdEventsEventIdSectionAliasesQueryOptions = <
  TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSectionAliases>>,
  TError = AxiosError<void>,
>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  params?: GetApiMarketplacesMarketplaceIdEventsEventIdSectionAliasesParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSectionAliases>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiMarketplacesMarketplaceIdEventsEventIdSectionAliasesQueryKey(marketplaceId, eventId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSectionAliases>>> = ({ signal }) =>
    getApiMarketplacesMarketplaceIdEventsEventIdSectionAliases(marketplaceId, eventId, params, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!(marketplaceId && eventId), ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSectionAliases>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetApiMarketplacesMarketplaceIdEventsEventIdSectionAliasesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSectionAliases>>
>;
export type GetApiMarketplacesMarketplaceIdEventsEventIdSectionAliasesQueryError = AxiosError<void>;

export const useGetApiMarketplacesMarketplaceIdEventsEventIdSectionAliases = <
  TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSectionAliases>>,
  TError = AxiosError<void>,
>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  params?: GetApiMarketplacesMarketplaceIdEventsEventIdSectionAliasesParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSectionAliases>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiMarketplacesMarketplaceIdEventsEventIdSectionAliasesQueryOptions(marketplaceId, eventId, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getApiMarketplacesMarketplaceIdEventsEventIdSellerListings = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.get(`/api/Marketplaces/${marketplaceId}/Events/${eventId}/SellerListings`, options);
};

export const getGetApiMarketplacesMarketplaceIdEventsEventIdSellerListingsQueryKey = (marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace, eventId: string) => {
  return [`/api/Marketplaces/${marketplaceId}/Events/${eventId}/SellerListings`] as const;
};

export const getGetApiMarketplacesMarketplaceIdEventsEventIdSellerListingsQueryOptions = <
  TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSellerListings>>,
  TError = AxiosError<void>,
>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSellerListings>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiMarketplacesMarketplaceIdEventsEventIdSellerListingsQueryKey(marketplaceId, eventId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSellerListings>>> = ({ signal }) =>
    getApiMarketplacesMarketplaceIdEventsEventIdSellerListings(marketplaceId, eventId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!(marketplaceId && eventId), ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSellerListings>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetApiMarketplacesMarketplaceIdEventsEventIdSellerListingsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSellerListings>>
>;
export type GetApiMarketplacesMarketplaceIdEventsEventIdSellerListingsQueryError = AxiosError<void>;

export const useGetApiMarketplacesMarketplaceIdEventsEventIdSellerListings = <
  TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSellerListings>>,
  TError = AxiosError<void>,
>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSellerListings>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiMarketplacesMarketplaceIdEventsEventIdSellerListingsQueryOptions(marketplaceId, eventId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getApiMarketplacesMarketplaceIdEventsEventId = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsMarketplacesEvent>> => {
  return axios.get(`/api/Marketplaces/${marketplaceId}/Events/${eventId}`, options);
};

export const getGetApiMarketplacesMarketplaceIdEventsEventIdQueryKey = (marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace, eventId: string) => {
  return [`/api/Marketplaces/${marketplaceId}/Events/${eventId}`] as const;
};

export const getGetApiMarketplacesMarketplaceIdEventsEventIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventId>>,
  TError = AxiosError<void>,
>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventId>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiMarketplacesMarketplaceIdEventsEventIdQueryKey(marketplaceId, eventId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventId>>> = ({ signal }) =>
    getApiMarketplacesMarketplaceIdEventsEventId(marketplaceId, eventId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!(marketplaceId && eventId), ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventId>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetApiMarketplacesMarketplaceIdEventsEventIdQueryResult = NonNullable<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventId>>>;
export type GetApiMarketplacesMarketplaceIdEventsEventIdQueryError = AxiosError<void>;

export const useGetApiMarketplacesMarketplaceIdEventsEventId = <TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventId>>, TError = AxiosError<void>>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventId>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiMarketplacesMarketplaceIdEventsEventIdQueryOptions(marketplaceId, eventId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getApiMarketplacesMarketplaceIdEvents = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  params: GetApiMarketplacesMarketplaceIdEventsParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsMarketplacesEvent[]>> => {
  return axios.get(`/api/Marketplaces/${marketplaceId}/Events`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getGetApiMarketplacesMarketplaceIdEventsQueryKey = (marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace, params: GetApiMarketplacesMarketplaceIdEventsParams) => {
  return [`/api/Marketplaces/${marketplaceId}/Events`, ...(params ? [params] : [])] as const;
};

export const getGetApiMarketplacesMarketplaceIdEventsQueryOptions = <TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEvents>>, TError = AxiosError<void>>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  params: GetApiMarketplacesMarketplaceIdEventsParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEvents>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiMarketplacesMarketplaceIdEventsQueryKey(marketplaceId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEvents>>> = ({ signal }) =>
    getApiMarketplacesMarketplaceIdEvents(marketplaceId, params, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!marketplaceId, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEvents>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetApiMarketplacesMarketplaceIdEventsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEvents>>>;
export type GetApiMarketplacesMarketplaceIdEventsQueryError = AxiosError<void>;

export const useGetApiMarketplacesMarketplaceIdEvents = <TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEvents>>, TError = AxiosError<void>>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  params: GetApiMarketplacesMarketplaceIdEventsParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEvents>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiMarketplacesMarketplaceIdEventsQueryOptions(marketplaceId, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getApiMarketplacesMarketplaceIdEventsEventIdSnapshots = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsWarehouseListingSnapshot[]>> => {
  return axios.get(`/api/Marketplaces/${marketplaceId}/Events/${eventId}/Snapshots`, options);
};

export const getGetApiMarketplacesMarketplaceIdEventsEventIdSnapshotsQueryKey = (marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace, eventId: string) => {
  return [`/api/Marketplaces/${marketplaceId}/Events/${eventId}/Snapshots`] as const;
};

export const getGetApiMarketplacesMarketplaceIdEventsEventIdSnapshotsQueryOptions = <
  TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSnapshots>>,
  TError = AxiosError<void>,
>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSnapshots>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiMarketplacesMarketplaceIdEventsEventIdSnapshotsQueryKey(marketplaceId, eventId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSnapshots>>> = ({ signal }) =>
    getApiMarketplacesMarketplaceIdEventsEventIdSnapshots(marketplaceId, eventId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!(marketplaceId && eventId), ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSnapshots>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetApiMarketplacesMarketplaceIdEventsEventIdSnapshotsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSnapshots>>>;
export type GetApiMarketplacesMarketplaceIdEventsEventIdSnapshotsQueryError = AxiosError<void>;

export const useGetApiMarketplacesMarketplaceIdEventsEventIdSnapshots = <
  TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSnapshots>>,
  TError = AxiosError<void>,
>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdEventsEventIdSnapshots>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiMarketplacesMarketplaceIdEventsEventIdSnapshotsQueryOptions(marketplaceId, eventId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFiltered = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  brokerNerdsBarkerCoreModelsPricingRuleFilters: BrokerNerdsBarkerCoreModelsPricingRuleFilters,
  params?: PutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFilteredParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsWarehouseListingSnapshot[]>> => {
  return axios.put(`/api/Marketplaces/${marketplaceId}/Events/${eventId}/Snapshots/Filtered`, brokerNerdsBarkerCoreModelsPricingRuleFilters, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getPutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFilteredMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFiltered>>,
    TError,
    {
      marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace;
      eventId: string;
      data: BrokerNerdsBarkerCoreModelsPricingRuleFilters;
      params?: PutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFilteredParams;
    },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFiltered>>,
  TError,
  {
    marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace;
    eventId: string;
    data: BrokerNerdsBarkerCoreModelsPricingRuleFilters;
    params?: PutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFilteredParams;
  },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFiltered>>,
    {
      marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace;
      eventId: string;
      data: BrokerNerdsBarkerCoreModelsPricingRuleFilters;
      params?: PutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFilteredParams;
    }
  > = (props) => {
    const { marketplaceId, eventId, data, params } = props ?? {};

    return putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFiltered(marketplaceId, eventId, data, params, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFilteredMutationResult = NonNullable<
  Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFiltered>>
>;
export type PutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFilteredMutationBody = BrokerNerdsBarkerCoreModelsPricingRuleFilters;
export type PutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFilteredMutationError = AxiosError<void>;

export const usePutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFiltered = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFiltered>>,
    TError,
    {
      marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace;
      eventId: string;
      data: BrokerNerdsBarkerCoreModelsPricingRuleFilters;
      params?: PutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFilteredParams;
    },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFiltered>>,
  TError,
  {
    marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace;
    eventId: string;
    data: BrokerNerdsBarkerCoreModelsPricingRuleFilters;
    params?: PutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFilteredParams;
  },
  TContext
> => {
  const mutationOptions = getPutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFilteredMutationOptions(options);

  return useMutation(mutationOptions);
};
export const putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsRecent = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  brokerNerdsBarkerCoreModelsPricingRuleFilters: BrokerNerdsBarkerCoreModelsPricingRuleFilters,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsWarehouseListingSnapshot[]>> => {
  return axios.put(`/api/Marketplaces/${marketplaceId}/Events/${eventId}/Snapshots/Recent`, brokerNerdsBarkerCoreModelsPricingRuleFilters, options);
};

export const getPutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsRecentMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsRecent>>,
    TError,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; eventId: string; data: BrokerNerdsBarkerCoreModelsPricingRuleFilters },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsRecent>>,
  TError,
  { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; eventId: string; data: BrokerNerdsBarkerCoreModelsPricingRuleFilters },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsRecent>>,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; eventId: string; data: BrokerNerdsBarkerCoreModelsPricingRuleFilters }
  > = (props) => {
    const { marketplaceId, eventId, data } = props ?? {};

    return putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsRecent(marketplaceId, eventId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsRecentMutationResult = NonNullable<
  Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsRecent>>
>;
export type PutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsRecentMutationBody = BrokerNerdsBarkerCoreModelsPricingRuleFilters;
export type PutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsRecentMutationError = AxiosError<void>;

export const usePutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsRecent = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsRecent>>,
    TError,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; eventId: string; data: BrokerNerdsBarkerCoreModelsPricingRuleFilters },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsRecent>>,
  TError,
  { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; eventId: string; data: BrokerNerdsBarkerCoreModelsPricingRuleFilters },
  TContext
> => {
  const mutationOptions = getPutApiMarketplacesMarketplaceIdEventsEventIdSnapshotsRecentMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postApiMarketplacesMarketplaceIdEventsEventIdBoost = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  eventId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.post(`/api/Marketplaces/${marketplaceId}/Events/${eventId}/Boost`, undefined, options);
};

export const getPostApiMarketplacesMarketplaceIdEventsEventIdBoostMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postApiMarketplacesMarketplaceIdEventsEventIdBoost>>,
    TError,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; eventId: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postApiMarketplacesMarketplaceIdEventsEventIdBoost>>,
  TError,
  { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; eventId: string },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postApiMarketplacesMarketplaceIdEventsEventIdBoost>>,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; eventId: string }
  > = (props) => {
    const { marketplaceId, eventId } = props ?? {};

    return postApiMarketplacesMarketplaceIdEventsEventIdBoost(marketplaceId, eventId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiMarketplacesMarketplaceIdEventsEventIdBoostMutationResult = NonNullable<Awaited<ReturnType<typeof postApiMarketplacesMarketplaceIdEventsEventIdBoost>>>;

export type PostApiMarketplacesMarketplaceIdEventsEventIdBoostMutationError = AxiosError<void>;

export const usePostApiMarketplacesMarketplaceIdEventsEventIdBoost = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postApiMarketplacesMarketplaceIdEventsEventIdBoost>>,
    TError,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; eventId: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof postApiMarketplacesMarketplaceIdEventsEventIdBoost>>,
  TError,
  { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; eventId: string },
  TContext
> => {
  const mutationOptions = getPostApiMarketplacesMarketplaceIdEventsEventIdBoostMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getApiMarketplacesMarketplaceIdBoosts = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerOrleansStateIntervalOverride[]>> => {
  return axios.get(`/api/Marketplaces/${marketplaceId}/Boosts`, options);
};

export const getGetApiMarketplacesMarketplaceIdBoostsQueryKey = (marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace) => {
  return [`/api/Marketplaces/${marketplaceId}/Boosts`] as const;
};

export const getGetApiMarketplacesMarketplaceIdBoostsQueryOptions = <TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdBoosts>>, TError = AxiosError<void>>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdBoosts>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiMarketplacesMarketplaceIdBoostsQueryKey(marketplaceId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdBoosts>>> = ({ signal }) =>
    getApiMarketplacesMarketplaceIdBoosts(marketplaceId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!marketplaceId, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdBoosts>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetApiMarketplacesMarketplaceIdBoostsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdBoosts>>>;
export type GetApiMarketplacesMarketplaceIdBoostsQueryError = AxiosError<void>;

export const useGetApiMarketplacesMarketplaceIdBoosts = <TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdBoosts>>, TError = AxiosError<void>>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdBoosts>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiMarketplacesMarketplaceIdBoostsQueryOptions(marketplaceId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getApiMarketplacesMarketplaceIdBoostsEnabled = (marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> => {
  return axios.get(`/api/Marketplaces/${marketplaceId}/Boosts/Enabled`, options);
};

export const getGetApiMarketplacesMarketplaceIdBoostsEnabledQueryKey = (marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace) => {
  return [`/api/Marketplaces/${marketplaceId}/Boosts/Enabled`] as const;
};

export const getGetApiMarketplacesMarketplaceIdBoostsEnabledQueryOptions = <
  TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdBoostsEnabled>>,
  TError = AxiosError<void>,
>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdBoostsEnabled>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiMarketplacesMarketplaceIdBoostsEnabledQueryKey(marketplaceId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdBoostsEnabled>>> = ({ signal }) =>
    getApiMarketplacesMarketplaceIdBoostsEnabled(marketplaceId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!marketplaceId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdBoostsEnabled>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetApiMarketplacesMarketplaceIdBoostsEnabledQueryResult = NonNullable<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdBoostsEnabled>>>;
export type GetApiMarketplacesMarketplaceIdBoostsEnabledQueryError = AxiosError<void>;

export const useGetApiMarketplacesMarketplaceIdBoostsEnabled = <TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdBoostsEnabled>>, TError = AxiosError<void>>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdBoostsEnabled>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiMarketplacesMarketplaceIdBoostsEnabledQueryOptions(marketplaceId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const putApiMarketplacesMarketplaceIdBoostsEnabled = (marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> => {
  return axios.put(`/api/Marketplaces/${marketplaceId}/Boosts/Enabled`, undefined, options);
};

export const getPutApiMarketplacesMarketplaceIdBoostsEnabledMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdBoostsEnabled>>,
    TError,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdBoostsEnabled>>, TError, { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdBoostsEnabled>>, { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace }> = (
    props,
  ) => {
    const { marketplaceId } = props ?? {};

    return putApiMarketplacesMarketplaceIdBoostsEnabled(marketplaceId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutApiMarketplacesMarketplaceIdBoostsEnabledMutationResult = NonNullable<Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdBoostsEnabled>>>;

export type PutApiMarketplacesMarketplaceIdBoostsEnabledMutationError = AxiosError<void>;

export const usePutApiMarketplacesMarketplaceIdBoostsEnabled = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdBoostsEnabled>>,
    TError,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof putApiMarketplacesMarketplaceIdBoostsEnabled>>, TError, { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace }, TContext> => {
  const mutationOptions = getPutApiMarketplacesMarketplaceIdBoostsEnabledMutationOptions(options);

  return useMutation(mutationOptions);
};
export const deleteApiMarketplacesMarketplaceIdBoostsEnabled = (
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.delete(`/api/Marketplaces/${marketplaceId}/Boosts/Enabled`, options);
};

export const getDeleteApiMarketplacesMarketplaceIdBoostsEnabledMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteApiMarketplacesMarketplaceIdBoostsEnabled>>,
    TError,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof deleteApiMarketplacesMarketplaceIdBoostsEnabled>>, TError, { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteApiMarketplacesMarketplaceIdBoostsEnabled>>, { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace }> = (
    props,
  ) => {
    const { marketplaceId } = props ?? {};

    return deleteApiMarketplacesMarketplaceIdBoostsEnabled(marketplaceId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteApiMarketplacesMarketplaceIdBoostsEnabledMutationResult = NonNullable<Awaited<ReturnType<typeof deleteApiMarketplacesMarketplaceIdBoostsEnabled>>>;

export type DeleteApiMarketplacesMarketplaceIdBoostsEnabledMutationError = AxiosError<void>;

export const useDeleteApiMarketplacesMarketplaceIdBoostsEnabled = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteApiMarketplacesMarketplaceIdBoostsEnabled>>,
    TError,
    { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof deleteApiMarketplacesMarketplaceIdBoostsEnabled>>, TError, { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace }, TContext> => {
  const mutationOptions = getDeleteApiMarketplacesMarketplaceIdBoostsEnabledMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getApiMarketplacesMarketplaceIdStatus = (marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace, options?: AxiosRequestConfig): Promise<AxiosResponse<string>> => {
  return axios.get(`/api/Marketplaces/${marketplaceId}/Status`, options);
};

export const getGetApiMarketplacesMarketplaceIdStatusQueryKey = (marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace) => {
  return [`/api/Marketplaces/${marketplaceId}/Status`] as const;
};

export const getGetApiMarketplacesMarketplaceIdStatusQueryOptions = <TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdStatus>>, TError = AxiosError<unknown>>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdStatus>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiMarketplacesMarketplaceIdStatusQueryKey(marketplaceId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdStatus>>> = ({ signal }) =>
    getApiMarketplacesMarketplaceIdStatus(marketplaceId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!marketplaceId, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdStatus>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetApiMarketplacesMarketplaceIdStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdStatus>>>;
export type GetApiMarketplacesMarketplaceIdStatusQueryError = AxiosError<unknown>;

export const useGetApiMarketplacesMarketplaceIdStatus = <TData = Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdStatus>>, TError = AxiosError<unknown>>(
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiMarketplacesMarketplaceIdStatus>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiMarketplacesMarketplaceIdStatusQueryOptions(marketplaceId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};
